import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtISODate } from "@/lib/dates";
import { Plus, ScanLine, Lock, History, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const RISK_TYPES = [
  "Environmental Risk Assessment", "Moving and Handling Risk Assessment", "Falls Risk Assessment",
  "Medication Risk Assessment", "Behaviour and Emotional Regulation", "Road Safety / Community Access",
  "Nutrition and Hydration", "Mental Health Crisis", "Positive Risk Taking",
  "COSHH", "Infection Control", "Generic / Custom",
];

const CLOSURE_REASONS = [
  ["risk_eliminated", "Risk eliminated"],
  ["risk_no_longer_applicable", "Risk no longer applicable"],
  ["service_user_discharged", "Service user discharged"],
  ["risk_transferred", "Risk transferred to new provider"],
  ["replaced_by_updated", "Replaced by updated assessment"],
  ["other", "Other"],
];

export default function RiskAssessments() {
  const { user } = useAuth();
  const isSupervisory = ["registered_manager", "deputy_manager", "team_leader", "super_admin"].includes(user.role);
  const isRm = ["registered_manager", "deputy_manager", "super_admin"].includes(user.role);

  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [closing, setClosing] = useState(null);
  const [history, setHistory] = useState(null);
  const [showSuperseded, setShowSuperseded] = useState(false);

  const [form, setForm] = useState({
    service_user_id: "", risk_type: RISK_TYPES[0], description: "", persons_at_risk: "Service user",
    likelihood: 3, impact: 3, control_measures: "", residual_likelihood: 2, residual_impact: 2,
    actions_required: "", review_date: fmtISODate(new Date(Date.now() + 90 * 86400000)),
  });

  const load = () => api.get("/risk-assessments").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); api.get("/service-users").then(({ data }) => setUsers(data.items || [])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/risk-assessments", form);
    setCreating(false);
    load();
  };

  const score = form.likelihood * form.impact;
  const residual = form.residual_likelihood * form.residual_impact;

  const visible = items.filter((r) => showSuperseded || r.status !== "superseded");

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Risk assessments</h1>
          <p className="text-sm text-slate-600">CQC Regulation 12 · likelihood × impact scoring · version history retained · never deleted, only closed.</p>
        </div>
        <div className="flex gap-2">
          <label className="inline-flex items-center gap-1 text-xs text-slate-600">
            <input type="checkbox" checked={showSuperseded} onChange={(e) => setShowSuperseded(e.target.checked)} /> Show superseded
          </label>
          <button onClick={() => setCreating((v) => !v)} data-testid="new-risk-button" className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[44px]">
            <Plus className="h-4 w-4" /> New assessment
          </button>
        </div>
      </div>

      {creating && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <select required value={form.service_user_id} onChange={(e) => setForm({ ...form, service_user_id: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
            <option value="">Service user…</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <select value={form.risk_type} onChange={(e) => setForm({ ...form, risk_type: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
            {RISK_TYPES.map((r) => <option key={r}>{r}</option>)}
          </select>
          <textarea required placeholder="Risk description / hazard" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md" />
          <div><label className="text-xs uppercase font-semibold text-slate-500">Likelihood</label><input type="range" min="1" max="5" value={form.likelihood} onChange={(e) => setForm({ ...form, likelihood: +e.target.value })} className="w-full" /><p className="text-sm">{form.likelihood}</p></div>
          <div><label className="text-xs uppercase font-semibold text-slate-500">Impact</label><input type="range" min="1" max="5" value={form.impact} onChange={(e) => setForm({ ...form, impact: +e.target.value })} className="w-full" /><p className="text-sm">{form.impact}</p></div>
          <div className="md:col-span-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm">Risk score: <strong>{score}</strong> · Residual: <strong>{residual}</strong></div>
          <textarea required placeholder="Control measures in place" rows={3} value={form.control_measures} onChange={(e) => setForm({ ...form, control_measures: e.target.value })} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md" />
          <div><label className="text-xs uppercase font-semibold text-slate-500">Residual likelihood</label><input type="range" min="1" max="5" value={form.residual_likelihood} onChange={(e) => setForm({ ...form, residual_likelihood: +e.target.value })} className="w-full" /><p className="text-sm">{form.residual_likelihood}</p></div>
          <div><label className="text-xs uppercase font-semibold text-slate-500">Residual impact</label><input type="range" min="1" max="5" value={form.residual_impact} onChange={(e) => setForm({ ...form, residual_impact: +e.target.value })} className="w-full" /><p className="text-sm">{form.residual_impact}</p></div>
          <input type="date" required value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <textarea placeholder="Actions required" rows={2} value={form.actions_required} onChange={(e) => setForm({ ...form, actions_required: e.target.value })} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md" />
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border border-slate-300 rounded-md">Cancel</button>
            <button type="submit" data-testid="risk-submit-button" className="px-4 py-2 bg-[#0D7377] text-white rounded-md font-medium">Save assessment</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.map((r) => {
          const due = new Date(r.review_date);
          const overdue = due < new Date() && r.status === "active";
          const dueSoon = !overdue && (due - new Date()) < 14 * 86400000 && r.status === "active";
          return (
            <div key={r.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 cc-glow" data-testid={`risk-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">{r.risk_type} · v{r.version_number || 1}</p>
                  <p className="font-heading text-base font-semibold text-slate-900 mt-0.5"><ScanLine className="h-4 w-4 inline mr-1" /> {r.description}</p>
                </div>
                <StatusBadge status={overdue ? "overdue" : dueSoon ? "in_progress" : (r.status || "active")} label={overdue ? "Overdue" : dueSoon ? "Review due" : r.status?.replace(/_/g, " ")} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded-md p-3">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Initial score</p>
                  <p className="font-heading font-bold text-lg">{r.risk_score} · {r.risk_band}</p>
                </div>
                <div className="bg-slate-50 rounded-md p-3">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Residual</p>
                  <p className="font-heading font-bold text-lg">{r.residual_score} · {r.residual_band}</p>
                </div>
              </div>
              {r.status === "closed" && (
                <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-700">
                  <strong>Closed:</strong> {r.closure_reason?.replace(/_/g, " ")} · {r.closed_by_name} · {fmtDate(r.closed_at)}
                </div>
              )}
              {r.status === "pending_closure" && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
                  <strong>Pending RM countersignature</strong> · submitted by {r.closed_by_name}
                  {isRm && (
                    <button onClick={async () => { await api.post(`/risk-assessments/${r.id}/countersign-closure`); load(); }} data-testid={`countersign-${r.id}`} className="ml-2 px-2 py-0.5 bg-amber-600 text-white rounded text-xs">Countersign &amp; close</button>
                  )}
                </div>
              )}
              <p className="mt-2 text-xs text-slate-500">Review by {fmtDate(r.review_date)}</p>
              <div className="mt-3 flex gap-2 text-xs">
                <button onClick={() => api.get(`/risk-assessments/${r.id}/history`).then(({ data }) => setHistory({ ra: r, items: data.items }))} className="inline-flex items-center gap-1 text-[#0D7377] font-medium" data-testid={`risk-history-${r.id}`}>
                  <History className="h-3 w-3" /> Version history
                </button>
                {isSupervisory && r.status === "active" && (
                  <button onClick={() => setClosing(r)} data-testid={`risk-close-${r.id}`} className="inline-flex items-center gap-1 text-red-700 font-medium ml-auto">
                    <Lock className="h-3 w-3" /> Close assessment
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {visible.length === 0 && <p className="text-sm text-slate-500">No risk assessments.</p>}
      </div>

      {closing && <ClosureModal ra={closing} onClose={() => setClosing(null)} reload={load} isRm={isRm} />}
      {history && <HistoryModal data={history} onClose={() => setHistory(null)} />}
    </div>
  );
}

function ClosureModal({ ra, onClose, reload, isRm }) {
  const [form, setForm] = useState({ closure_reason: "risk_eliminated", closure_narrative: "", final_residual_rating: "low", confirmation: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post(`/risk-assessments/${ra.id}/close`, form);
      setDone(data);
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="closure-success">
          <h3 className="font-heading text-lg font-semibold text-slate-900">Closure submitted</h3>
          <p className="text-sm text-slate-600 mt-2">
            {done.requires_countersignature
              ? "This closure has been submitted and is now pending Registered Manager / Deputy countersignature before it is finalised."
              : "The risk assessment has been closed. It will remain permanently on the service user's record."}
          </p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-[#0D7377] text-white rounded-md font-medium">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="risk-closure-modal">
        <header className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <p className="text-xs uppercase tracking-wider text-red-700">Close risk assessment</p>
            <h3 className="font-heading text-xl font-semibold text-slate-900">{ra.risk_type}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-5 w-5" /></button>
        </header>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Closure reason</label>
            <select required value={form.closure_reason} onChange={(e) => setForm({ ...form, closure_reason: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" data-testid="closure-reason">
              {CLOSURE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Closure narrative (min 50 characters)</label>
            <textarea required minLength={50} rows={5} value={form.closure_narrative} onChange={(e) => setForm({ ...form, closure_narrative: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" data-testid="closure-narrative" />
            <p className="text-xs text-slate-500 mt-1">{form.closure_narrative.length}/50 minimum</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Final residual risk rating</label>
            <select required value={form.final_residual_rating} onChange={(e) => setForm({ ...form, final_residual_rating: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" data-testid="final-residual">
              <option value="low">Low</option>
              <option value="negligible">Negligible</option>
              <option value="none">None</option>
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" required checked={form.confirmation} onChange={(e) => setForm({ ...form, confirmation: e.target.checked })} data-testid="closure-confirmation" />
            <span>I confirm this risk assessment is no longer required to inform active care delivery.</span>
          </label>
          {!isRm && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">As a Team Leader, your closure submission will require RM/Deputy countersignature before finalising.</p>}
          {error && <p className="text-sm text-red-700">{error}</p>}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-md">Cancel</button>
            <button type="submit" disabled={busy} data-testid="closure-submit" className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium">Submit closure</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HistoryModal({ data, onClose }) {
  const items = (data.items || []).slice().sort((a, b) => (b.version_number || 1) - (a.version_number || 1));
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="risk-history-modal">
        <header className="flex items-start justify-between p-5 border-b border-slate-200">
          <h3 className="font-heading text-xl font-semibold text-slate-900">Version history — {data.ra.risk_type}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-5 w-5" /></button>
        </header>
        <ul className="divide-y divide-slate-100">
          {items.map((v) => (
            <li key={v.id} className="p-4">
              <div className="flex items-center justify-between">
                <p className="font-medium text-slate-900">Version {v.version_number || 1}</p>
                <StatusBadge status={v.status} label={v.status?.replace(/_/g, " ")} />
              </div>
              <p className="text-xs text-slate-500 mt-1">{fmtDate(v.created_at)} · {v.completed_by_name}</p>
              <p className="text-sm text-slate-700 mt-2">Score {v.risk_score} ({v.risk_band}) · Residual {v.residual_score} ({v.residual_band})</p>
              {v.review_note && <p className="text-xs italic text-slate-600 mt-1">"{v.review_note}"</p>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
